import { logToExternalErrorHandlers } from '@luminovo/commons';

// A pre-deploy tab fails its next lazy import; we don't reload for the user, who may be mid-draft.
// It was the most-reported error (LUMIQUOTE-FRONTEND-68G); `StaleAppReloadPrompt` lets the user pick the moment.

/** Vite dispatches this on `window` when a preloaded chunk fails to load. */
const VITE_PRELOAD_ERROR = 'vite:preloadError';

type StaleAppListener = () => void;

const listeners = new Set<StaleAppListener>();
let isStale = false;

/** True once a chunk load has failed in a way that only a reload can fix. */
export function isAppStale(): boolean {
    return isStale;
}

/** Also runs `listener` at once if the app is already stale: a chunk can fail before the subscriber mounts. */
export function subscribeToStaleApp(listener: StaleAppListener): () => void {
    listeners.add(listener);

    if (isStale) {
        listener();
    }

    return () => {
        listeners.delete(listener);
    };
}

export function markAppStale(): void {
    if (isStale) {
        return;
    }
    isStale = true;

    for (const listener of listeners) {
        listener();
    }
}

/** Call once, before the app renders; `preventDefault()` stops Vite rethrowing an error per failed chunk. */
export function installStaleAppDetection(): void {
    window.addEventListener(VITE_PRELOAD_ERROR, (event) => {
        event.preventDefault();

        const payload = event as Event & { payload?: unknown };
        // An `info` event for each failed chunk, in place of the error Vite would have rethrown.
        logToExternalErrorHandlers(new Error('App is stale: a chunk from a previous deployment is gone'), {
            level: 'info',
            extra: { reason: payload.payload instanceof Error ? payload.payload.message : String(payload.payload) },
        });

        markAppStale();
    });
}
